import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const BeasKund = () => {
  const tourData = {
    name: "Beas Kund Trek",
    duration: "04 Nights 05 Days",
    price: "₹ 4,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935198/uzuo6rxsy5i5yk5ca3ck.webp",
    altitude: "12,139 ft",
    difficulty: "Moderate",
    region: "Himachal Pradesh",
    highlights: ["Sacred lake", "Mountain views", "Alpine meadows"],
    bestTime: "May to October",
    description: "The Beas Kund Trek is a beautiful moderate trek in Himachal Pradesh that takes you to the sacred source of the Beas River. This trek offers stunning mountain views, alpine meadows, and a chance to visit one of the most sacred lakes in the Himalayas."
  };

  return (
    <>
      <Helmet>
        <title>Beas Kund Trek - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Beas Kund Trek in Himachal Pradesh. Sacred lake trek with stunning mountain views and alpine meadows." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Sacred lake trek with stunning mountain views and alpine meadows
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek takes you to the sacred source of the Beas River, one of the most 
                    important rivers in North India. The journey offers spectacular views of the 
                    surrounding peaks and passes through beautiful alpine meadows. It's a perfect 
                    choice for those looking for a moderate trek with spiritual significance.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Sacred source of Beas River</li>
                    <li>Stunning mountain views</li>
                    <li>Beautiful alpine meadows</li>
                    <li>Moderate difficulty level</li>
                    <li>Spiritual significance</li>
                    <li>Perfect for beginners</li>
                    <li>Photography opportunities</li>
                    <li>Mountain camping experience</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Arrival at Manali</h4>
                    <ul>
                      <li>Manali, situated towards the lower end of the valley, is one of the biggest tourist attraction in Himachal Pradesh and one of the best holiday destinations in the world.</li>
                      <li>Just hearing the name "Manali" paints a picture of waterfall, song birds, forest land, apple orchards and snow clad glaciated mountains.</li>
                      <li>Manali also has a fascinating legend behind it. Manu Samhita, and this is the place where Manu got landed on Earth after the great deluge.</li>
                      <li>Later, stroll through the energetic Mall Road, populated with quaint cafes, local handicraft shops, and numerous street food stalls.</li>
                      <li>Have a hot dinner and retire to your hotel for the night as you prepare for the Beas Kund trekking adventure.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: From Manali to Dhundi to Bakarthach</h4>
                    <ul>
                      <li>Post breakfast, we board our vehicle and proceed toward Dhundi while traversing the famous adventure destination Solang Nala.</li>
                      <li>An hour's drive from Manali brings us to Dhundi, which serves as the official starting point of the Beas Kund trek.</li>
                      <li>The picturesque route from Solang to the village of Dhundi traces the right bank (north) of the river and winds through dense forests of pines, walnut, and silver birch trees.</li>
                      <li>Once we arrive to Dhundi we start hiking. The trail from Dhundi moves upstream along the river in a gentle and picturesque manner until we reach Bakarthach, which lies about 5 km ahead.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: From Bakarthach to Beas Kund and Back</h4>
                    <ul>
                      <li>Today we set off after breakfast and start making our way up to reach the final point of the Beas Kund trek.</li>
                      <li>The trail leads through beautiful meadows and offers spectacular views of the surrounding peaks.</li>
                      <li>We reach the sacred Beas Kund Lake, the source of River Beas, and spend time exploring the area and taking in the breathtaking views.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Return Journey</h4>
                    <ul>
                      <li>After breakfast, we begin our descent back to Dhundi.</li>
                      <li>The return journey allows us to appreciate the beauty of the landscape from a different perspective.</li>
                      <li>We reach Dhundi and then drive back to Manali, marking the end of our memorable Beas Kund trek adventure.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Rucksack with rain cover</li>
                        <li>Day pack bag</li>
                        <li>Head torch with spare batteries</li>
                        <li>UV protection sunglasses</li>
                        <li>Water bottles: Two 1-liter bottles</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Non-skid, deep treaded, high-ankle trekking shoes: 1 pair</li>
                        <li>Lightweight slippers or sandals: 1 pair</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Quick-dry warm lower or track pants: 2</li>
                        <li>Full-sleeve T-shirts/sweatshirts: 1 for every 2 days of trekking</li>
                        <li>Thick woolen socks: 1 pair for every 2 days of trekking</li>
                        <li>Thermal body warmer (upper & lower): 1 set</li>
                        <li>Undergarments: 1 for each day of trekking</li>
                        <li>Warm jacket (closed at wrist & neck): 1</li>
                        <li>Full-sleeve sweater: 1</li>
                        <li>Rainwear (jacket & pants): 1 set</li>
                        <li>Waterproof, warm gloves: 1 pair</li>
                        <li>Woolen cap: 1</li>
                        <li>Sun-shielding hat: 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Personal toiletries kit: Small towel, toilet paper, paper soap, bar soap, toothbrush, toothpaste, cold cream, etc.</li>
                        <li>Sunscreen lotion: Small pack</li>
                        <li>Lip balm: Small pack</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Small, lightweight, leak-proof lunch box: 1</li>
                        <li>Plate: 1</li>
                        <li>Spoon: 1</li>
                        <li>Tea/coffee mug (plastic): 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Personal medicines: Bring plenty if needed; consult your doctor before the trek.</li>
                        <li className="optional">Dry fruits, nuts, chocolate bars (Optional)</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Accommodation in hotels/camps on sharing basis</li>
                    <li>All meals (breakfast, lunch, dinner) during the trek</li>
                    <li>Transportation from Manali to Dhundi and back</li>
                    <li>Professional trek guide and support staff</li>
                    <li>Trekking permits and forest fees</li>
                    <li>Camping equipment (tents, sleeping bags, mattresses)</li>
                    <li>First aid kit and emergency support</li>
                    <li>All necessary trekking arrangements</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Personal expenses and tips</li>
                    <li>Any additional meals or beverages</li>
                    <li>Personal trekking gear and equipment</li>
                    <li>Travel insurance</li>
                    <li>Any expenses due to weather conditions or natural calamities</li>
                    <li>Expenses for evacuation in case of emergency</li>
                    <li>Any other expenses not mentioned in inclusions</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>12,139 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>20 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Moderate</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>May to October</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Rishikesh)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 5,999 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 6,499 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 6,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Rishikesh:</strong> Rishikesh bus stand or railway station between 07:00 AM to 08:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Sacred source of Beas River</li>
                    <li>Stunning mountain views</li>
                    <li>Beautiful alpine meadows</li>
                    <li>Moderate difficulty level</li>
                    <li>Spiritual significance</li>
                    <li>Perfect for beginners</li>
                    <li>Photography opportunities</li>
                    <li>Mountain camping experience</li>
                    <li>Professional guide and support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default BeasKund;
